import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { DashboardLoadErrorState } from '@/features/creators/dashboard/components/load-error';
import {
	LOAD_ERROR_TEXT,
	NAV_TEXT,
} from '@/features/creators/dashboard/content';
import { BareDashboardLayout } from '@/features/creators/dashboard/layout/bare-dashboard-layout';
import { DashboardLayout } from '@/features/creators/dashboard/layout/dashboard-layout';
import { CreatorsApiError } from '@/features/creators/dashboard/services/creators-api';
import { fetchCreatorSummary } from '@/features/creators/dashboard/services/fetch-creator-summary';
import { PageLoader } from '@/features/creators/shared/page-loader';
import { CREATORS_PATHS } from '@/features/creators/shared/paths';
// The dashboard paints its own fixed dark palette (like the creators landing
// and apply pages), so it reuses CBCC's pin to keep the shell's adaptive OS
// theme from tinting it.
import {
	ForceLightScheme,
	forceLightSchemeScript,
} from '@/features/travel-credit-card/layout/force-light-scheme';
import { seo } from '@/utils/seo';

// /ixi-finance/creators/dashboard — the signed-in creator dashboard. Behind `_auth`: the
// guard runs the login funnel first. This layout owns the header, tab bar and
// desktop sidebar, and loads the creator's earnings summary: the code for the
// header and sidebar, plus what Home and Payout show. Each tab is a child route.
// A withdrawal's router.invalidate() reloads it.
export const Route = createFileRoute('/_auth/ixi-finance/creators_/dashboard')({
	head: () => ({
		meta: seo({ title: NAV_TEXT.pageTitle }),
		scripts: [{ children: forceLightSchemeScript }],
	}),
	loader: async () => {
		try {
			return await fetchCreatorSummary();
		} catch (error) {
			// Not a creator yet, or not approved: onboarding shows the form or
			// where their application stands.
			if (
				error instanceof CreatorsApiError &&
				(error.code === 'CREATOR_NOT_FOUND' ||
					error.code === 'CREATOR_NOT_APPROVED')
			) {
				throw redirect({ to: CREATORS_PATHS.onboarding, replace: true });
			}
			throw error;
		}
	},
	component: DashboardRoute,
	pendingComponent: DashboardPending,
	// Every tab needs the summary, so without it there's no dashboard to show.
	errorComponent: DashboardLoadError,
	ssr: false,
	// 'stay' means the URL changed without a route transition (the Withdraw
	// sheet's #withdraw hash, or moving between tabs). Skip the reload so the
	// sheet doesn't refetch the summary; router.invalidate() still reloads it.
	shouldReload: ({ cause }) => cause !== 'stay',
});

function DashboardPending() {
	return (
		<>
			<ForceLightScheme />
			<BareDashboardLayout>
				<PageLoader />
			</BareDashboardLayout>
		</>
	);
}

function DashboardLoadError() {
	return (
		<>
			<ForceLightScheme />
			<BareDashboardLayout>
				<DashboardLoadErrorState what={LOAD_ERROR_TEXT.dashboard} />
			</BareDashboardLayout>
		</>
	);
}

function DashboardRoute() {
	const { profile } = Route.useLoaderData();
	return (
		<>
			<ForceLightScheme />
			<DashboardLayout profile={profile}>
				<Outlet />
			</DashboardLayout>
		</>
	);
}
